import cookie from 'cookie'
import { NextPage, NextPageContext } from 'next'
import Head from 'next/head'
import UAParser from 'ua-parser-js'

import HomesForSale from 'modules/HomesForSale'

import { getLocationDataFromRequest, setLocationCookiesServerSide } from 'utils/locationHelpers'

interface IProps {
  latitude?: number
  longitude?: number
  serverFetchError?: string
}

const HomesForSalePage: NextPage<IProps> = ({ latitude, longitude, serverFetchError }) => {
  if (serverFetchError) console.error(serverFetchError)

  return (
    <>
      <Head>
        <title>Homes For Sale | HomeLister</title>
        <meta
          name="description"
          content="Find the right home for you. Check out our featured and current homes for sale by owner at HomeLister."
        />
        <meta
          name="keywords"
          content="homes for sale, houses for sale, HomeLister"
        />
        <meta
          name="og:description"
          property="og:description"
          content="Find the right home for you. Check out our featured and current homes for sale by owner at HomeLister."
        />
      </Head>
      <HomesForSale
        ipLatitude={latitude}
        ipLongitude={longitude}
      />
    </>
  )
}

export const getServerSideProps = async (ctx: NextPageContext) => {
  /**
   * On Safari and Firefox, getting location from the browser works well (sub 2s), but on Chrome it
   * takes at least 5 but sometimes 10s+ to get the location, so if we detect a request from Chrome
   * we will use the IP address to get the location instead.
   *
   * We only do this for Chrome in order to save credits on the IPData API, and because asking the
   * user for explicit location permission is preferred.
   *
   * As another optimization, if the user has already visited a page on the site that calls the IPData
   * API, we will use the location from that request stored in their cookie instead of making
   * another request, if it's less than 1 day old. This intentionally still has lower precedence
   * than re-requesting browser location on the client if user is on an approved browser.
   */
  try {
    // 1. test for browser
    const parser = new UAParser(ctx.req?.headers['user-agent'] || '')
    const { browser } = parser.getResult()
    if (browser.name !== 'Chrome') return { props: {} }

    // 2. test for cookie
    const parsedCookies = cookie.parse(ctx.req?.headers.cookie || '')

    if (parsedCookies) {
      const { latitude, longitude } = parsedCookies

      if (latitude && longitude) {
        return {
          props: {
            latitude: Number(latitude),
            longitude: Number(longitude),
          },
        }
      }
    }

    // 3. get location from IP
    const {
      latitude,
      longitude,
      country_code: countryCode,
    } = await getLocationDataFromRequest(ctx.req)

    // we store the lat/lng location here so we can use it on future requests in step 2
    setLocationCookiesServerSide(latitude, longitude, ctx.res)

    if (countryCode !== 'US') return { props: {} }

    return {
      props: {
        latitude,
        longitude,
      },
    }
  } catch (err: any) {
    const error = err as Error
    return { props: { serverFetchError: error.message } }
  }
}

export default HomesForSalePage
